import { Box } from "@mui/material";
import StandardAnalyticsCard from "features/analytics/components/StandardAnalyticsCard";
import CarouselCard from "Components/UI/CarouselCard";
import {
  AnalyticsCardPanelHeader,
  StandardAnalyticsChartStatCard,
} from "features/analytics";
import {
  CHART_KEY_DASHBOARD_PULSE_CONVERSIONS,
  CHART_KEY_DASHBOARD_PULSE_EFFECTIVENESS_RATE,
  CHART_KEY_DASHBOARD_PULSE_FUNNEL,
  CHART_KEY_DASHBOARD_PULSE_ORGANIC_SESSIONS,
  CHART_KEY_DASHBOARD_PULSE_OVERALL_CONTRIBUTION_MARGIN,
  CHART_KEY_DASHBOARD_PULSE_OVERALL_MER,
  CHART_KEY_DASHBOARD_PULSE_OVERALL_ROI,
  CHART_KEY_DASHBOARD_PULSE_PAID_SESSIONS,
  CHART_KEY_DASHBOARD_PULSE_SESSIONS_CONVERSIONS,
  CHART_KEY_DASHBOARD_PULSE_SESSIONS_REVENUE,
  CHART_KEY_DASHBOARD_PULSE_TOTAL_REVENUE,
} from "features/analytics/constants/chartKeys";
import {
  ApiDimension,
  ApiChartTemplate,
  ApiChartType,
  ApiMetric,
  ApiYAxisChartType,
  ApiChartAttribute,
  ApiProjectType,
} from "@incendium/api";
import { TrendingUp } from "@mui/icons-material";
import { useIsMobile } from "Hooks/useIsMobile";
import { useMemo } from "react";
import { useHasProjectType } from "features/project";
import { SalesFlowFunnelChart } from "features/salesFlow";

interface IPulseTabProps {
  attributes: ApiChartAttribute[];
}

function PulseTab({ attributes }: IPulseTabProps) {
  const isMobile = useIsMobile();
  const hasProjectType = useHasProjectType();
  const isEcommerce = useMemo(
    () => hasProjectType(ApiProjectType.TYPE_ECOMMERCE),
    [hasProjectType],
  );

  if (!isEcommerce) {
    return (
      <>
        <CarouselCard
          title="Key Stats"
          emptyMessage={"No key stats available"}
          items={[
            <StandardAnalyticsChartStatCard
              chartKey={CHART_KEY_DASHBOARD_PULSE_ORGANIC_SESSIONS}
              staticAttributes={attributes}
              chart={{
                name: "No. of Organic Sessions",
                dimension: [ApiDimension.DIMENSION_NOT_SET],
                template: ApiChartTemplate.SNAPSHOT,
                type: ApiChartType.STAT_CARD,
                attributes: [],
                yAxisKeys: [
                  {
                    key: "l",
                    fields: [ApiMetric.METRIC_ORGANIC_SESSION_COUNT],
                  },
                ],
                displayOptions: {
                  statTitle: "No. of Organic Sessions",
                },
                comparison: true,
              }}
            />,
            <StandardAnalyticsChartStatCard
              chartKey={CHART_KEY_DASHBOARD_PULSE_PAID_SESSIONS}
              staticAttributes={attributes}
              chart={{
                name: "No. of Paid Sessions",
                dimension: [ApiDimension.DIMENSION_NOT_SET],
                template: ApiChartTemplate.SNAPSHOT,
                type: ApiChartType.STAT_CARD,
                comparison: true,
                attributes: [],
                includePublisherReadyOnly: true,
                displayOptions: {
                  statTitle: "No. of Paid Sessions",
                },
                yAxisKeys: [
                  {
                    key: "l",
                    fields: [ApiMetric.METRIC_PAID_SESSION_COUNT],
                  },
                ],
              }}
            />,
            <StandardAnalyticsChartStatCard
              chartKey={CHART_KEY_DASHBOARD_PULSE_EFFECTIVENESS_RATE}
              staticAttributes={attributes}
              chart={{
                name: "Effectiveness Rate",
                dimension: [ApiDimension.DIMENSION_NOT_SET],
                template: ApiChartTemplate.SNAPSHOT,
                type: ApiChartType.STAT_CARD,
                attributes: [],
                includePublisherReadyOnly: true,
                yAxisKeys: [
                  {
                    key: "l",
                    fields: [ApiMetric.METRIC_EFFECTIVE_SESSION_RATE],
                  },
                ],
                displayOptions: {
                  statTitle: "Effectiveness Rate",
                },
                comparison: true,
              }}
            />,
            <StandardAnalyticsChartStatCard
              chartKey={CHART_KEY_DASHBOARD_PULSE_CONVERSIONS}
              staticAttributes={attributes}
              chart={{
                name: "No. of Conversions",
                dimension: [ApiDimension.DIMENSION_NOT_SET],
                template: ApiChartTemplate.SNAPSHOT,
                type: ApiChartType.STAT_CARD,
                attributes: [],
                includePublisherReadyOnly: true,
                yAxisKeys: [
                  {
                    key: "l",
                    fields: [ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT],
                  },
                ],
                displayOptions: {
                  statTitle: "No. of Conversion",
                },
                comparison: true,
              }}
            />,
          ]}
          loading={false}
        />

        <SalesFlowFunnelChart attributes={attributes} ignoreAttrModel />

        <Box sx={{ height: 400, flexShrink: 0 }}>
          <StandardAnalyticsCard
            chartKey={CHART_KEY_DASHBOARD_PULSE_SESSIONS_CONVERSIONS}
            staticAttributes={attributes}
            ignoreAttrModel
            customHeader={
              <AnalyticsCardPanelHeader
                icon={<TrendingUp fontSize="small" />}
                title="Sessions & Conversions"
              />
            }
            chart={{
              name: "Sessions & Conversions",
              dimension: [ApiDimension.DIMENSION_SESSION_START_BY_DAY],
              template: ApiChartTemplate.TREND,
              type: ApiChartType.GRAPH,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_SESSIONS_COUNT],
                },
                {
                  key: "r",
                  fields: [ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT],
                  chart: {
                    [ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT]:
                      ApiYAxisChartType.AREA,
                  },
                },
              ],
              displayOptions: {
                noFilters: true,
              },
            }}
          />
        </Box>
      </>
    );
  }
  return (
    <>
      <CarouselCard
        title="Key Stats"
        emptyMessage={"No key stats available"}
        items={[
          <StandardAnalyticsChartStatCard
            chartKey={CHART_KEY_DASHBOARD_PULSE_TOTAL_REVENUE}
            staticAttributes={attributes}
            chart={{
              name: "Total Revenue",
              dimension: [ApiDimension.DIMENSION_NOT_SET],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.STAT_CARD,
              attributes: [],
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_REVENUE],
                },
              ],
              displayOptions: {
                statTitle: "Total Revenue",
              },
              comparison: true,
            }}
          />,
          <StandardAnalyticsChartStatCard
            chartKey={CHART_KEY_DASHBOARD_PULSE_OVERALL_CONTRIBUTION_MARGIN}
            staticAttributes={attributes}
            chart={{
              name: "Overall Contribution Margin",
              dimension: [ApiDimension.DIMENSION_NOT_SET],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.STAT_CARD,
              comparison: true,
              attributes: [],
              includePublisherReadyOnly: true,
              displayOptions: {
                statTitle: "Overall Contribution Margin",
              },
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_SESSION_CONTRIBUTION_MARGIN],
                },
              ],
            }}
          />,
          <StandardAnalyticsChartStatCard
            chartKey={CHART_KEY_DASHBOARD_PULSE_OVERALL_ROI}
            staticAttributes={attributes}
            chart={{
              name: "Overall ROI",
              dimension: [ApiDimension.DIMENSION_NOT_SET],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.STAT_CARD,
              attributes: [],
              includePublisherReadyOnly: true,
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_ROI],
                },
              ],
              displayOptions: {
                statTitle: "Overall ROI",
              },
              comparison: true,
            }}
          />,
          <StandardAnalyticsChartStatCard
            chartKey={CHART_KEY_DASHBOARD_PULSE_OVERALL_MER}
            staticAttributes={attributes}
            chart={{
              name: "Overall MER",
              dimension: [ApiDimension.DIMENSION_NOT_SET],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.STAT_CARD,
              attributes: [],
              includePublisherReadyOnly: true,
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_SESSION_EFFICIENCY_RATIO],
                },
              ],
              displayOptions: {
                statTitle: "Overall MER",
              },
              comparison: true,
            }}
          />,
        ]}
        loading={false}
      />

      <Box
        sx={{
          height: 400,
          flexShrink: 0,
          overflow: "auto",
        }}
      >
        <StandardAnalyticsCard
          chartKey={CHART_KEY_DASHBOARD_PULSE_FUNNEL}
          staticAttributes={attributes}
          ignoreAttrModel
          chart={{
            name: "Ecommerce Conversion Funnel",
            dimension: [],
            template: ApiChartTemplate.SNAPSHOT,
            type: ApiChartType.FUNNEL,
            comparison: true,
            attributes: [],
            displayOptions: {
              funnelUseLogFn: true,
              noAttributionToggle: true,
              funnelAlwaysShowAdditionalMetrics: isMobile,
              metricNames: [
                {
                  metric: ApiMetric.METRIC_SESSION_ADD_TO_CART_FIRED,
                  name: "Sessions with Add to Cart",
                },
                {
                  metric: ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_COUNT,
                  name: "Total Add to Carts",
                },
                {
                  metric: ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_RATE,
                  name: "Add to Cart Rate",
                },
                {
                  metric: ApiMetric.METRIC_SALES_COUNT_LAST,
                  name: "Total Sales",
                },
                {
                  metric: ApiMetric.METRIC_REVENUE_LAST,
                  name: "Total Revenue",
                },
                {
                  metric: ApiMetric.METRIC_NEW_CUSTOMERS_COUNT_LAST,
                  name: "Total New Customers",
                },
                {
                  metric: ApiMetric.METRIC_AVERAGE_SALE_VALUE_LAST,
                  name: "AOV",
                },
                {
                  metric: ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT,
                  name: "Total Product Pageviews",
                },
                {
                  metric: ApiMetric.METRIC_SESSION_PRODUCT_PAGE_VIEWED,
                  name: "Sessions where PDP viewed",
                },
                {
                  metric: ApiMetric.METRIC_SESSION_PRODUCT_PAGE_VIEWED_RATE,
                  name: "PDP viewed rate",
                },
                {
                  metric: ApiMetric.METRIC_CHECKOUT_START_COUNT,
                  name: "Sessions with Checkout Start",
                },
              ],
              funnelAdditonalMetrics: [
                {
                  key: ApiMetric.METRIC_SESSIONS_COUNT,
                  metrics: [
                    ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
                    ApiMetric.METRIC_PAID_SESSION_COUNT,
                    ApiMetric.METRIC_ORGANIC_SESSION_COUNT,
                  ],
                },
                {
                  key: ApiMetric.METRIC_SESSION_PRODUCT_PAGE_VIEWED,
                  metrics: [
                    ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT,
                    ApiMetric.METRIC_SESSION_PRODUCT_PAGE_VIEWED_RATE,
                    ApiMetric.METRIC_PRODUCT_PAGE_EFFECTIVENESS_RATE,
                    ApiMetric.METRIC_PRODUCT_IMPRESSIONS_COUNT,
                  ],
                },
                {
                  key: ApiMetric.METRIC_SESSION_ADD_TO_CART_FIRED,
                  metrics: [
                    ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_COUNT,
                    ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_RATE,
                    ApiMetric.METRIC_CART_ABANDONMENT_RATE,
                  ],
                },
                {
                  key: ApiMetric.METRIC_CHECKOUT_START_COUNT,
                  metrics: [
                    ApiMetric.METRIC_CHECKOUT_START_RATE,
                    ApiMetric.METRIC_CHECKOUT_ABANDONMENT_RATE,
                  ],
                },
                {
                  key: ApiMetric.METRIC_SALES_COUNT_LAST,
                  metrics: [
                    ApiMetric.METRIC_REVENUE_LAST,
                    ApiMetric.METRIC_NEW_CUSTOMERS_COUNT_LAST,
                    ApiMetric.METRIC_AVERAGE_SALE_VALUE_LAST,
                  ],
                },
              ],
            },
            yAxisKeys: [
              {
                key: "l",
                fields: [
                  ApiMetric.METRIC_SESSIONS_COUNT,
                  ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
                  ApiMetric.METRIC_PAID_SESSION_COUNT,
                  ApiMetric.METRIC_ORGANIC_SESSION_COUNT,
                  ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT,
                  ApiMetric.METRIC_SESSION_PRODUCT_PAGE_VIEWED,
                  ApiMetric.METRIC_SESSION_PRODUCT_PAGE_VIEWED_RATE,
                  ApiMetric.METRIC_PRODUCT_PAGE_EFFECTIVENESS_RATE,
                  ApiMetric.METRIC_PRODUCT_IMPRESSIONS_COUNT,
                  ApiMetric.METRIC_SESSION_ADD_TO_CART_FIRED,
                  ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_COUNT,
                  ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_RATE,
                  ApiMetric.METRIC_CART_ABANDONMENT_RATE,
                  ApiMetric.METRIC_CHECKOUT_START_COUNT,
                  ApiMetric.METRIC_CHECKOUT_START_RATE,
                  ApiMetric.METRIC_CHECKOUT_ABANDONMENT_RATE,
                  ApiMetric.METRIC_SALES_COUNT_LAST,
                  ApiMetric.METRIC_REVENUE_LAST,
                  ApiMetric.METRIC_NEW_CUSTOMERS_COUNT_LAST,
                  ApiMetric.METRIC_AVERAGE_SALE_VALUE_LAST,
                ],
              },
            ],
          }}
        />
      </Box>
      <Box sx={{ height: 400, flexShrink: 0 }}>
        <StandardAnalyticsCard
          chartKey={CHART_KEY_DASHBOARD_PULSE_SESSIONS_REVENUE}
          staticAttributes={attributes}
          ignoreAttrModel
          customHeader={
            <AnalyticsCardPanelHeader
              icon={<TrendingUp fontSize="small" />}
              title="Sessions & Revenue"
            />
          }
          chart={{
            name: "Sessions & Revenue",
            dimension: [ApiDimension.DIMENSION_SESSION_START_BY_DAY],
            template: ApiChartTemplate.TREND,
            type: ApiChartType.GRAPH,
            attributes: [],
            yAxisKeys: [
              {
                key: "l",
                fields: [ApiMetric.METRIC_SESSIONS_COUNT],
              },
              {
                key: "r",
                fields: [ApiMetric.METRIC_REVENUE],
                chart: {
                  [ApiMetric.METRIC_REVENUE]: ApiYAxisChartType.AREA,
                },
              },
            ],
            displayOptions: {
              noFilters: true,
            },
          }}
        />
      </Box>
    </>
  );
}

export default PulseTab;
